import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import ImageAudioButton from '@design-system/image-audio-button';
import Canvas from '@design-system/canvas';
import { ETheme } from '@datatype/type';
import { replaceDashWithTilde } from '@util/string';
import { useTypedSelector } from '@hook/common/useStore';

interface IProps {
  sentence: string;
  pronunciation?: string;
  translation: string;
  explanation?: string;
  audioLink: string | null;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 100%;
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;

const LineCardContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;

const ConversationKeyLine: React.FC<IProps> = ({ sentence, pronunciation, translation, explanation, audioLink }) => {
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isJapanese = studyLanguage === EApiLanguageCode.JAPANESE;

  return (
    <Wrapper>
      <WelcomeContainer>
        <TextBlock
          text={'이 표현 내것으로 만들어볼까요?'}
          design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'center' } }}
        />
        <TextBlock
          text={'소리 내어 따라 읽어보세요'}
          design={{ text: { size: 'medium', color: color.GRAY_1, bold: false }, box: { alignX: 'center' } }}
        />
      </WelcomeContainer>
      <Gap height={size.MARGIN_1} />
      <Canvas theme={ETheme.BROWN} shape='round' width='100%' padding={size.MARGIN_1}>
        <LineCardContainer>
          <TextBlock
            text={sentence}
            annotation={isJapanese && pronunciation ? { reading: pronunciation } : undefined}
            design={{
              text: { size: 'large', color: color.BLACK_2, bold: true },
              box: { width: '100%', alignX: 'left' },
            }}
          />
          <Gap height={'5px'} />
          <TextBlock
            text={replaceDashWithTilde(translation)}
            design={{
              text: { size: 'medium', color: color.BROWN_2, bold: true },
              box: { width: '100%', alignX: 'left' },
            }}
          />
          {explanation && (
            <>
              <Gap height={'5px'} />
              <TextBlock
                text={replaceDashWithTilde(explanation)}
                design={{
                  text: { size: 'small', color: color.GRAY_1, bold: false },
                  box: { width: '100%', alignX: 'left' },
                }}
              />
            </>
          )}
          {audioLink && (
            <>
              <Gap height={'10px'} />
              <ImageAudioButton
                theme={ETheme.BROWN}
                kind='outlined'
                size='small'
                shape='round'
                width='fit-content'
                sources={[audioLink]}
              />
            </>
          )}
        </LineCardContainer>
      </Canvas>
    </Wrapper>
  );
};

export default ConversationKeyLine;
